import Serializer, {
  type SerializeArguments,
  type DeserializeArguments,
} from './Serializer';
import { type Element, Node } from 'slate';
import wrapNonBlocksInContainer from '../helpers/wrapNonBlocksInContainer';
import { getStyleFromChildMarks } from '../../util/getStyleFromChildMarks';
import React from 'react';
import {
  type ListElement,
  type ListItemElement,
  type ParagraphElement,
} from '../../types/aha-editor';
import { shouldHideMarker } from '../../plugins/ListPlugin/utils';
import { isList, isListItem } from '../../plugins/ListPlugin/guards';
import { isHTMLElementNode } from '../helpers/serializerHelpers';
import classNames from 'classnames';

/**
 * Returns true if el is a list wrapper element we added when
 * serializing, false otherwise.
 */
function isListWrapper(el: HTMLElement) {
  return (
    el.style.listStyleType === 'none' &&
    el.children.length === 1 &&
    ['ul', 'ol'].includes(el.children[0].tagName.toLowerCase())
  );
}

class SerializerListItem extends Serializer {
  static serialize({
    node,
    path,
    children,
    context,
  }: SerializeArguments): React.ReactNode {
    if (!isListItem(node)) return;

    const listParent = Node.parent(context.editor, path) as ListElement; // TODO: Previously this checked the type of the parnt. Why?
    const checklistParent = listParent.type === 'checklist' ? listParent : null;
    const style = getStyleFromChildMarks(node);

    const hideMarker = shouldHideMarker(node);

    if (!checklistParent) {
      let Tag: 'li' | 'dt' | 'dd' = 'li';
      if (listParent.type === 'dl_list') {
        // Use the child index to alternate the tag.
        if (path[path.length - 1] % 2 === 0) {
          Tag = 'dt';
        } else {
          Tag = 'dd';
        }
      }

      const listItemClasses = classNames({
        'aha-editor-v2__li--hide-marker': hideMarker,
      });

      const otherProps: { className?: string } = {};
      if (listItemClasses) {
        otherProps.className = listItemClasses;
      }

      return (
        <Tag {...otherProps} style={style}>
          {children}
        </Tag>
      );
    }

    const checklistItemClasses = classNames({
      'checklist-item--checked': node.list_checked,
      'checklist-item--unchecked': !node.list_checked,
    });

    let indicator = '';
    if (!hideMarker) {
      indicator = node.list_checked ? '\u2611' : '\u2610';
    }

    let listItemContent = (
      <>
        <div className="checklist-item--content">
          <span className="checklist-item__indicator">{indicator}</span>
          {children.slice(0, children.length - 1)}
        </div>
        {children[children.length - 1]}
      </>
    );

    const lastChild = node.children[node.children.length - 1];
    if (!isList(lastChild)) {
      listItemContent = (
        <>
          <div className="checklist-item--content">
            <span className="checklist-item__indicator">{indicator}</span>
            {children}
          </div>
        </>
      );
    }

    return (
      <li className={checklistItemClasses} style={style}>
        {listItemContent}
      </li>
    );
  }

  static deserialize({
    el,
    markAttributes,
    next,
    context,
  }: DeserializeArguments): ListItemElement | Element[] | undefined | null {
    if (!isHTMLElementNode(el)) return;
    if (!context.editor) return;

    // skip checklist indicators entirely
    if (
      el.tagName.toLowerCase() === 'span' &&
      el.classList.contains('checklist-item__indicator')
    ) {
      return [];
    }

    if (!['li', 'dd', 'dt'].includes(el.tagName.toLowerCase())) return;

    // If this is an li that we added to wrap a deeply nested list,
    // ignore it.
    if (isListWrapper(el)) {
      return;
    }

    // TinyMCE likes to use text nodes inside lists, which our Slate
    // schema doesn't support. So we need to wrap all top-level runs
    // of non-Block nodes in paragraph nodes.
    const children = wrapNonBlocksInContainer<ParagraphElement>(
      context.editor,
      next(el.childNodes, markAttributes),
      'paragraph'
    );

    const isChecklistItem =
      el.classList.contains('checklist-item--checked') ||
      el.classList.contains('checklist-item--unchecked');
    if (!isChecklistItem) {
      return {
        type: 'list_item',
        children,
      };
    } else {
      return {
        type: 'list_item',
        list_checked: el.classList.contains('checklist-item--checked'),
        children,
      };
    }
  }
}

export default SerializerListItem;
